import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, RotateCcw, AlertTriangle, Calendar, Building2, Tag, CreditCard, StickyNote, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CATEGORY_LABELS } from '@/constants/categories';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function TrashPage() {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('entries');
  const [confirmDialog, setConfirmDialog] = useState(null);

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, usersRes] = await Promise.all([
        api.get('/trash/entries'),
        api.get('/trash/users'),
      ]);
      setEntries(entriesRes.data);
      setUsers(usersRes.data);
    } catch {
      toast.error('Failed to load trash');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const handleRestore = async (type, id) => {
    try {
      await api.post(`/trash/${type}/${id}/restore`);
      toast.success('Restored successfully');
      fetchTrash();
    } catch {
      toast.error('Failed to restore');
    }
  };

  const handlePermanentDelete = async () => {
    if (!confirmDialog) return;
    try {
      await api.delete(`/trash/${confirmDialog.type}/${confirmDialog.id}/permanent`);
      toast.success('Permanently deleted');
      setConfirmDialog(null);
      fetchTrash();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Trash / Recycle Bin</h1>
        <p className="text-sm text-muted-foreground mt-1">Recover accidentally deleted items or remove them permanently</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border" data-testid="trash-tabs">
        <button
          onClick={() => setActiveTab('entries')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'entries' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="trash-tab-entries"
        >
          Entries ({entries.length})
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'users' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="trash-tab-users"
        >
          Users ({users.length})
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Loading...</div>
      ) : activeTab === 'entries' ? (
        <EntriesTrash entries={entries} onRestore={(id) => handleRestore('entries', id)} onPermanentDelete={(id) => setConfirmDialog({ type: 'entries', id })} />
      ) : (
        <UsersTrash users={users} onRestore={(id) => handleRestore('users', id)} />
      )}

      {/* Permanent Delete Confirmation */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="permanent-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Permanent Delete
            </DialogTitle>
            <DialogDescription>
              This action <strong>cannot be undone</strong>. The data will be permanently removed from the database.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmDialog(null)} data-testid="cancel-permanent-delete">Cancel</Button>
            <Button variant="destructive" onClick={handlePermanentDelete} data-testid="confirm-permanent-delete">Delete Forever</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EntriesTrash({ entries, onRestore, onPermanentDelete }) {
  if (entries.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="trash-entries-empty">
        <Trash2 className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground font-medium">Trash is empty</p>
        <p className="text-xs text-muted-foreground/70 mt-1">Deleted entries will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="trash-entries-list">
      {entries.map(entry => (
        <div key={entry._id} className="bg-card border border-border p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`trash-entry-${entry._id}`}>
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 ${entry.entry_type === 'income' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400'}`}>
                {entry.entry_type === 'income' ? 'Money In' : 'Money Out'}
              </span>
              <span className={`text-lg font-bold font-mono ${entry.entry_type === 'income' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                {'\u20B9'}{entry.amount?.toLocaleString('en-IN')}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{entry.date}</span>
              <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{CATEGORY_LABELS[entry.category] || entry.category}</span>
              {entry.entity_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{entry.entity_name}</span>}
              <span className="flex items-center gap-1 capitalize"><CreditCard className="w-3 h-3" />{entry.payment_mode}</span>
            </div>
            {entry.notes && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <StickyNote className="w-3 h-3 shrink-0" />
                <span className="truncate">{entry.notes}</span>
              </div>
            )}
            {entry.deleted_at && (
              <div className="text-[10px] text-red-500/70">
                Deleted: {new Date(entry.deleted_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-900 dark:hover:bg-emerald-950/30" onClick={() => onRestore(entry._id)} data-testid={`restore-entry-${entry._id}`}>
              <RotateCcw className="w-3.5 h-3.5" /> Restore
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-950/30" onClick={() => onPermanentDelete(entry._id)} data-testid={`permanent-delete-entry-${entry._id}`}>
              <Trash2 className="w-3.5 h-3.5" /> Delete Forever
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function UsersTrash({ users, onRestore }) {
  if (users.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="trash-users-empty">
        <User className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground font-medium">No deleted users</p>
        <p className="text-xs text-muted-foreground/70 mt-1">Deleted user accounts will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="trash-users-list">
      {users.map(u => (
        <div key={u._id} className="bg-card border border-border p-4 flex items-center gap-4" data-testid={`trash-user-${u._id}`}>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">{u.name || 'Unnamed'}</div>
            <div className="text-xs text-muted-foreground">{u.email} &middot; <span className="capitalize">{u.role}</span></div>
            {u.deleted_at && (
              <div className="text-[10px] text-red-500/70 mt-0.5">
                Deleted: {new Date(u.deleted_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-900 dark:hover:bg-emerald-950/30" onClick={() => onRestore(u._id)} data-testid={`restore-user-${u._id}`}>
            <RotateCcw className="w-3.5 h-3.5" /> Restore
          </Button>
        </div>
      ))}
    </div>
  );
}
